export function generateCss() {
  const num = [];
  for (let i = 1; i < 1000; i++) {
    num.push(`tr:nth-child(${i}){
          animation-delay: ${i * 0.06}s !important;
        }`);
  }

  return num.join(' ');
}

export function jsonCopy<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

export function downloadFile(url: string) {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = url.substr(url.lastIndexOf('/') + 1);
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor); // Remove the anchor element after clicking
}

export function hashStringToColor(str: string) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const color = `hsl(${hash % 360}, 70%, 60%)`; // Generate an HSL color
  return color;
}

export function createAvatar(name: string): HTMLDivElement {
  const initials = name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  const avatar = document.createElement('div');
  avatar.classList.add('avatar');
  avatar.textContent = initials;
  // avatar.style.backgroundColor = hashStringToColor(initials);

  return avatar;
}

export function b64toBlob(b64Data: string, contentType = '', sliceSize = 512) {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
}

export function downloadBase64File(base64Data: string, fileName: string | null): void {
  const anchor = document.createElement('a');
  anchor.href = base64Data;
  anchor.download = fileName || '';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

export function orderItemsByIds<T, K extends keyof T>(ids: T[K][], idkey: K, items: T[]): T[] {
  const idSet = new Set<T[K]>(ids);
  const idToItemMap = new Map<T[K], T>(items.map((item) => [item[idkey], item]));

  const orderedItems: T[] = [];

  for (const id of ids) {
    const item = idToItemMap.get(id);
    if (item) {
      orderedItems.push(item);
    }
  }

  for (const item of items) {
    if (!idSet.has(item[idkey])) {
      orderedItems.push(item);
    }
  }

  return orderedItems;
}

export function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function shallowEqual(obj1: any, obj2: any): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => obj1[key] === obj2[key]);
}

export function arraysOfObjectsEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((obj, i) => JSON.stringify(obj) === JSON.stringify(b[i]));
}

export function isDesktop(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  // Simple mobile keywords
  const isMobile = /iphone|ipad|android|mobile|blackberry|iemobile|silk/.test(ua);
  return !isMobile;
}

export function isChromeOnWindows(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  return ua.includes('windows') && ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr');
}

// is-equal.util.ts

export function isEqual(a: any, b: any): boolean {
  // Same reference or both NaN
  if (a === b) return true;
  if (Number.isNaN(a) && Number.isNaN(b)) return true;

  // Types differ
  if (typeof a !== typeof b) return false;

  // Null cases
  if (a === null || b === null) return false;

  // --- Date ---
  if (a instanceof Date && b instanceof Date) {
    return a.getTime() === b.getTime();
  }

  // --- Array ---
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) {
      if (!isEqual(a[i], b[i])) return false;
    }
    return true;
  }

  // --- Map ---
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [key, val] of a) {
      if (!b.has(key) || !isEqual(val, b.get(key))) return false;
    }
    return true;
  }

  // --- Set ---
  if (a instanceof Set && b instanceof Set) {
    if (a.size !== b.size) return false;
    for (const val of a) {
      if (!b.has(val)) return false;
    }
    return true;
  }

  // --- Object ---
  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) return false;

    for (const key of keysA) {
      if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
      if (!isEqual(a[key], b[key])) return false;
    }

    return true;
  }

  // Fallback for primitives/functions (strict equality failed earlier)
  return false;
}
